import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";

const ICONS = {
  smtp: Lucide.Mail,
  gateway: Lucide.CreditCard,
};

const ACTION_COLOR = {
  update: "text-amber-300",
  test_send: "text-purple-300",
  create: "text-emerald-300",
  delete: "text-rose-300",
};

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const params = {};
    if (target) params.target_type = target;
    if (action) params.action = action;
    const r = await api.get("/admin/audit-log", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [target, action]);

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">Audit Log</h1>
          <p className="text-slate-400 text-sm mt-1">
            Sensitive admin mutations — SMTP / IMAP / payment gateway changes — are recorded here with secrets redacted.
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          <select data-testid="audit-target-filter" value={target} onChange={(e) => setTarget(e.target.value)} className="bx-input py-1.5">
            <option value="">All targets</option>
            <option value="smtp">SMTP</option>
            <option value="gateway">Gateway</option>
          </select>
          <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className="bx-input py-1.5">
            <option value="">All actions</option>
            <option value="update">update</option>
            <option value="test_send">test_send</option>
            <option value="create">create</option>
            <option value="delete">delete</option>
          </select>
        </div>
      </div>

      <div className="bx-card mt-6 overflow-x-auto" data-testid="audit-log-table">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Target</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Actor</th>
              <th className="text-left p-3">Summary</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const Icon = ICONS[r.target_type] || Lucide.Activity;
              const expandedRow = expanded === r.id;
              const secrets = r.extra?.changed_secrets || [];
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setExpanded(expandedRow ? null : r.id)} data-testid={`audit-row-${r.id.slice(0, 8)}`}>
                    <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{new Date(r.ts).toLocaleString()}</td>
                    <td className="p-3"><span className="flex items-center gap-1.5 text-slate-200"><Icon size={13} className="text-purple-300" /> {r.target_type}{r.target_id && r.target_id !== r.target_type ? <span className="text-slate-500 text-xs">/ {r.target_id}</span> : null}</span></td>
                    <td className="p-3"><span className={`text-xs font-semibold uppercase ${ACTION_COLOR[r.action] || "text-slate-300"}`}>{r.action}</span></td>
                    <td className="p-3 text-xs text-slate-400 bx-mono">{r.actor_email || r.actor_id?.slice(0, 8) || "—"}</td>
                    <td className="p-3 text-xs text-slate-400">
                      {secrets.length > 0 && (
                        <span className="text-amber-300 inline-flex items-center gap-1"><Lucide.Key size={11} /> {secrets.join(", ")}</span>
                      )}
                      {r.extra?.to && <span className="ml-2">→ {r.extra.to}</span>}
                      {r.extra?.success === false && <span className="ml-2 text-rose-300">failed</span>}
                    </td>
                    <td className="p-3"><Lucide.ChevronDown size={14} className={`text-slate-500 transition ${expandedRow ? "rotate-180" : ""}`} /></td>
                  </tr>
                  {expandedRow && (
                    <tr className="border-t border-white/5 bg-black/30">
                      <td colSpan={6} className="p-4">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="text-slate-500 uppercase tracking-wider mb-1">After</div>
                            <pre className="bx-mono text-[11px] text-slate-300 bg-black/50 p-3 rounded overflow-x-auto bx-scrollbar">{JSON.stringify(r.after, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="text-slate-500 uppercase tracking-wider mb-1">Extra</div>
                            <pre className="bx-mono text-[11px] text-slate-300 bg-black/50 p-3 rounded overflow-x-auto bx-scrollbar">{JSON.stringify(r.extra, null, 2)}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan={6} className="p-12 text-center text-slate-500 text-sm">No audit entries yet — changes to SMTP / gateways will appear here.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
